/**
 * Chat Message
 *
 * Single message bubble (user or assistant)
 */

import { currentLocale } from '@/lib/locale';
import { User, Bot } from 'lucide-react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { cn } from '@/lib/utils';
import { ToolCallCard } from './ToolCallCard';

interface Message {
  messageId: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  toolCalls?: any[];
}

interface ChatMessageProps {
  message: Message;
  onConfirmAction: (messageId: string, toolCallIndex: number, confirmed: boolean) => void;
}

export function ChatMessage({ message, onConfirmAction }: ChatMessageProps) {
  const isUser = message.role === 'user';

  return (
    <div
      className={cn(
        'flex gap-3',
        isUser ? 'justify-end' : 'justify-start'
      )}
    >
      {!isUser && (
        <div className="shrink-0 h-8 w-8 rounded-full bg-primary flex items-center justify-center">
          <Bot className="h-5 w-5 text-primary-foreground" />
        </div>
      )}

      <div className={cn('flex flex-col gap-2', isUser ? 'items-end' : 'items-start')}>
        {/* Message bubble */}
        <div
          className={cn(
            'rounded-lg px-4 py-2 max-w-[80%]',
            isUser
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-foreground'
          )}
        >
          {isUser ? (
            <p className="text-sm whitespace-pre-wrap">{message.content}</p>
          ) : (
            <div className="text-sm [&_p]:mb-1 [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4 [&_strong]:font-semibold [&_code]:bg-black/10 [&_code]:rounded [&_code]:px-1 [&_code]:text-xs">
              <ReactMarkdown
                components={{
                  a: ({ href, children }) =>
                    href?.startsWith('/') ? (
                      <Link
                        to={href}
                        className="text-blue-600 dark:text-blue-400 underline"
                      >
                        {children}
                      </Link>
                    ) : (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 dark:text-blue-400 underline"
                      >
                        {children}
                      </a>
                    ),
                }}
              >
                {message.content}
              </ReactMarkdown>
            </div>
          )}
        </div>

        {/* Tool calls — only show if confirmation is needed or there was an error */}
        {message.toolCalls && message.toolCalls.length > 0 && (
          <div className="space-y-2 w-full max-w-md">
            {message.toolCalls.map((toolCall, originalIndex) =>
              toolCall.requiresConfirmation || toolCall.status === 'error' ? (
                <ToolCallCard
                  key={originalIndex}
                  toolCall={toolCall}
                  onConfirm={(confirmed) =>
                    onConfirmAction(message.messageId, originalIndex, confirmed)
                  }
                />
              ) : null
            )}
          </div>
        )}

        {/* Timestamp */}
        <span className="text-xs text-muted-foreground">
          {new Date(message.timestamp).toLocaleTimeString(currentLocale(), {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
      </div>

      {isUser && (
        <div className="shrink-0 h-8 w-8 rounded-full bg-secondary flex items-center justify-center">
          <User className="h-5 w-5 text-secondary-foreground" />
        </div>
      )}
    </div>
  );
}
